/*
This computer program, as defined in the Copyright, Designs and Patents Act 1998 and the Software Directive (2009/24/EC), 
is the copyright of Logic Valley Ltd, a wholly owned subsidiary of Marston (Holdings) Ltd. All rights are reserved.
*/
/* istanbul ignore file */

/* =========================
   Utility Functions
========================= */

/**
 * Safely access object properties with fallback
 * @param {Object} obj - The object to access
 * @param {string} key - The key to retrieve
 * @param {*} defaultValue - Default value if key doesn't exist (default: '')
 * @returns {*} The value at obj[key] or defaultValue
 */
export const getSafeValue = (obj, key, defaultValue = '') => {
  return obj?.[key] ?? defaultValue
}

/**
 * Extract observation ID from observation details
 * @param {Object} observationDetails - The observation object
 * @returns {string|number|null} The observation ID or null
 */
export const getObservationId = (observationDetails) => {
  return observationDetails?.id || observationDetails?.observationId || null
}

/**
 * Format template string by replacing placeholders
 * @param {string} template - Template string with __placeholder__ format
 * @param {Object} values - Object with key-value pairs for replacement
 * @returns {string} Formatted string
 *
 * @example
 * formatTemplate('Case: __caseNumber__', { caseNumber: '12345' })
 * // Returns: 'Case: 12345'
 */
export const formatTemplate = (template, values) => {
  let result = template
  Object.keys(values).forEach((key) => {
    result = result.replace(`__${key}__`, values[key])
  })
  return result
}

/**
 * Check if observation is missing required ID
 * @param {Object} observationDetails - The observation object
 * @returns {boolean} True if ID is missing
 */
export const isMissingObservationId = (observationDetails) => {
  return !getObservationId(observationDetails)
}

/**
 * Format an ISO-like timestamp to dd/MM/yyyy HH:mm:ss
 * @param {string|number|Date} value - The timestamp value
 * @returns {string} Formatted timestamp or empty string
 */
export const formatDateTime = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  const pad2 = (num) => String(num).padStart(2, '0')
  const day = pad2(date.getDate())
  const month = pad2(date.getMonth() + 1)
  const year = date.getFullYear()
  const hours = pad2(date.getHours())
  const minutes = pad2(date.getMinutes())
  const seconds = pad2(date.getSeconds())

  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`
}
